import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  Alert,
  AlertTitle,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  Badge,
  Popover,
  Tab,
  Tabs,
  Divider,
  Stack,
  LinearProgress,
} from '@mui/material';
import {
  Memory as MemoryIcon,
  Speed as SpeedIcon,
  DeviceThermostat as TempIcon,
  Storage as StorageIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  NetworkWifi as NetworkIcon,
  Dvr as GpuIcon,
  Memory as VramIcon,
  NetworkCheck as ReceivedIcon,
  Upload as SentIcon,
  Notifications as NotificationsIcon,
  Dashboard as DashboardIcon,
  Memory as CpuIcon,
  Storage as DiskIcon,
} from '@mui/icons-material';
import { CartesianGrid, Tooltip as ChartTooltip, ResponsiveContainer, AreaChart, Area, XAxis, YAxis } from 'recharts';
import { theme } from '../helpers/theme';
import { getMetricsByNodeId } from '../api/services/monitoring/Monitoring';

// Format number with appropriate unit suffix
const formatNumber = (value, unit) => {
  if (value === undefined || value === null) return '0';
  
  // For percentages, just show 1 decimal place
  if (unit === '%') return `${value.toFixed(1)}${unit}`;
  
  // Format based on size
  if (value < 1) return `${value.toFixed(2)}${unit}`;
  if (value < 10) return `${value.toFixed(1)}${unit}`;
  return `${Math.round(value)}${unit}`;
};

// Custom tooltip for charts that shows formatted values
const CustomTooltip = ({ active, payload, label, unit }) => {
  if (active && payload && payload.length) {
    return (
      <Paper sx={{ p: 1, bgcolor: 'rgba(255, 255, 255, 0.9)' }}>
        <Typography variant="body2">{label}</Typography>
        {payload.map((entry, index) => (
          <Typography key={index} variant="body2" sx={{ color: entry.color }}>
            {entry.name}: {formatNumber(entry.value, unit || '')}
          </Typography>
        ))}
      </Paper>
    );
  }
  return null;
};

const MetricCard = ({ icon, title, value, total, unit, percentValue, color, secondaryText }) => (
  <Card sx={{ height: '100%', boxShadow: theme.shadows[1], position: 'relative', overflow: 'visible' }}>
    <CardContent sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Box sx={{ 
          p: 1, 
          borderRadius: '50%', 
          bgcolor: `${color}15`,
          mr: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {icon}
        </Box>
        <Typography variant="body1" fontWeight="medium">{title}</Typography>
      </Box>
      
      <Typography variant="h4" sx={{ fontWeight: 'medium', mb: 1, color: theme.palette.text.primary }}>
        {formatNumber(value, unit)}
      </Typography>
      
      {secondaryText && (
        <Typography variant="body2" color="text.secondary">
          {secondaryText}
        </Typography>
      )}
      
      {percentValue !== undefined && (
        <Box sx={{ mt: 1 }}>
          <LinearProgress 
            variant="determinate" 
            value={percentValue} 
            sx={{ 
              height: 6, 
              borderRadius: 3,
              bgcolor: theme.palette.grey[200],
              '& .MuiLinearProgress-bar': {
                bgcolor: color,
              }
            }} 
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {formatNumber(percentValue, '%')} used
          </Typography>
        </Box>
      )}
    </CardContent>
  </Card>
);

const CompactChart = ({ data, dataKey, color, title, unit, currentValue, secondaryValue, icon }) => (
  <Card sx={{ 
    height: '100%', 
    width: '100%',
    boxShadow: theme.shadows[1], 
    display: 'flex', 
    flexDirection: 'column' 
  }}>
    <CardContent sx={{ 
      p: 2, 
      flex: '1 1 auto',
      display: 'flex', 
      flexDirection: 'column',
      height: '100%' 
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ 
            p: 1, 
            borderRadius: '50%', 
            bgcolor: `${color}15`, 
            mr: 1,
            display: 'flex'
          }}>
            {icon}
          </Box>
          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{title}</Typography>
        </Box>
        <Typography variant="h6" sx={{ color: color, fontWeight: 'medium' }}>
          {formatNumber(currentValue, unit)}
        </Typography>
      </Box>

      {secondaryValue && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {secondaryValue}
        </Typography>
      )}

      <Box sx={{ flex: 1, mt: 'auto', height: 130, minHeight: 130 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={`color${dataKey}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.8}/>
                <stop offset="95%" stopColor={color} stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis 
              dataKey="time" 
              stroke={theme.palette.text.secondary} 
              tick={{ fontSize: 10 }} 
              tickFormatter={(val) => val.split(':').slice(0, 2).join(':')}
            />
            <YAxis 
              stroke={theme.palette.text.secondary}
              tickFormatter={(value) => formatNumber(value, '')}
              tick={{ fontSize: 10 }}
              width={30}
              domain={['dataMin', 'dataMax']}
              {...(dataKey === 'cpu' || dataKey.includes('Util') ? { domain: [0, 100] } : {})}
              {...(dataKey === 'memory' ? { domain: [0, 100] } : {})}
            />
            <ChartTooltip content={<CustomTooltip unit={unit} />} />
            <Area 
              type="monotone" 
              dataKey={dataKey}
              name={title}
              stroke={color} 
              fillOpacity={1} 
              fill={`url(#color${dataKey})`} 
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </CardContent>
  </Card>
);

export default function Monitoring() {
  const params = useParams();
  const { nodeId, serverId, hostname } = params;
  const navigate = useNavigate();
  
  const [metricsData, setMetricsData] = useState([]);
  const [lastMetrics, setLastMetrics] = useState({
    cpu: { usage: 0, cores: 0 },
    memory: { total: 0, free: 0, utilization: 0 },
    disk: { total: 0, free: 0, utilization: 0 },
    network: { received: 0, sent: 0 },
    gpu: {
      count: 0,
      vendor: '',
      models: [],
      temperatures: [],
      utilizations: [],
      vramUtilizations: [],
      memoryClocks: [],
      coreClocks: [],
    }
  });
  const [isOnline, setIsOnline] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  
  const streamReaderRef = useRef(null);
  const readerActiveRef = useRef(true);
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleAlertClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleAlertClose = () => {
    setAnchorEl(null);
  };
  
  const openAlerts = Boolean(anchorEl);

  useEffect(() => {
    const targetNodeId = nodeId || serverId || hostname;
    if (!targetNodeId) {
      setIsLoading(true);
      navigate('/monitoring/d76af964-905a-404c-b4ef-050f9259a5a9', { replace: true });
      return;
    }
    setIsLoading(false);
  }, [nodeId, serverId, hostname, navigate]);

  useEffect(() => {
    const currentNodeId = nodeId || serverId;
    if (!currentNodeId) return;

    const setupMetricsStream = async () => {
      try {
        if (streamReaderRef.current) await streamReaderRef.current.cancel().catch(e => console.log('Error closing reader:', e));
        const { streamReader, error } = await getMetricsByNodeId(currentNodeId);
        if (error || !streamReader) {
          console.error('Error setting up metrics stream:', error || 'No stream reader');
          setIsOnline(false);
          return;
        }
        setIsOnline(true);
        streamReaderRef.current = streamReader;
        readerActiveRef.current = true;
        readMetricsStream(streamReader);
      } catch (e) {
        console.error('Exception in metrics streaming setup:', e);
        setIsOnline(false);
      }
    };

    const readMetricsStream = async (reader) => {
      try {
        while (readerActiveRef.current) {
          const { done, value } = await reader.read();
          if (done) break;
          if (value && value.result) processMetric(value);
        }
      } catch (e) {
        console.error('Error reading from metrics stream:', e);
        setIsOnline(false);
        if (readerActiveRef.current) setTimeout(setupMetricsStream, 5000);
      }
    };

    const processMetric = (data) => {
      if (!data || !data.result || !data.result.metrics) return;
      const metric = data.result.metrics;
      const type = metric.type;
      const time = new Date().toLocaleTimeString();

      setLastMetrics(prev => {
        const newMetrics = { ...prev };
        const updateAndChart = (key, value, chartDataKey, chartValue) => {
          newMetrics[key] = { ...newMetrics[key], ...value };
          updateChartData(time, { [chartDataKey]: chartValue });
        };

        switch (type) {
          case 'cpu':
            updateAndChart('cpu', { usage: metric.floatValue * 100 }, 'cpu', metric.floatValue * 100);
            break;
          case 'memory_total':
            newMetrics.memory.total = parseInt(metric.intValue) / 1024;
            if (newMetrics.memory.free > 0 && newMetrics.memory.total > 0) {
              const util = ((newMetrics.memory.total - newMetrics.memory.free) / newMetrics.memory.total) * 100;
              updateAndChart('memory', { utilization: util }, 'memory', util);
            }
            break;
          case 'memory_free':
            newMetrics.memory.free = parseInt(metric.intValue) / 1024;
            if (newMetrics.memory.free > 0 && newMetrics.memory.total > 0) {
              const util = ((newMetrics.memory.total - newMetrics.memory.free) / newMetrics.memory.total) * 100;
              updateAndChart('memory', { utilization: util }, 'memory', util);
            }
            break;
          case 'disk_total':
            newMetrics.disk.total = parseInt(metric.intValue);
            if (newMetrics.disk.free > 0 && newMetrics.disk.total > 0) {
              const util = ((newMetrics.disk.total - newMetrics.disk.free) / newMetrics.disk.total) * 100;
              updateAndChart('disk', { utilization: util }, 'disk', util);
            }
            break;
          case 'disk_free':
            newMetrics.disk.free = parseInt(metric.intValue);
            if (newMetrics.disk.free > 0 && newMetrics.disk.total > 0) {
              const util = ((newMetrics.disk.total - newMetrics.disk.free) / newMetrics.disk.total) * 100;
              updateAndChart('disk', { utilization: util }, 'disk', util);
            }
            break;
          case 'network_received':
            updateAndChart('network', { received: metric.floatValue / (1024 * 1024) }, 'networkReceived', metric.floatValue / (1024 * 1024));
            break;
          case 'network_sent':
            updateAndChart('network', { sent: metric.floatValue / (1024 * 1024) }, 'networkSent', metric.floatValue / (1024 * 1024));
            break;
          case 'gpu_count': newMetrics.gpu.count = metric.floatValue || 1; break;
          case 'gpu_vendor': newMetrics.gpu.vendor = metric.stringValue; break;
          default:
            if (type.startsWith('gpu_')) {
              const indexMatch = type.match(/gpu_(\w+)_(\d+)/);
              if (indexMatch) {
                const metricName = indexMatch[1];
                const gpuIndex = parseInt(indexMatch[2]);
                if (!newMetrics.gpu.count) newMetrics.gpu.count = gpuIndex + 1;
                ['models', 'temperatures', 'utilizations', 'vramUtilizations', 'memoryClocks', 'coreClocks'].forEach(prop => {
                  if (!newMetrics.gpu[prop]) newMetrics.gpu[prop] = [];
                });

                switch (metricName) {
                  case 'model': newMetrics.gpu.models[gpuIndex] = metric.stringValue; break;
                  case 'temperature': 
                    newMetrics.gpu.temperatures[gpuIndex] = metric.floatValue;
                    updateChartData(time, { [`gpuTemp${gpuIndex}`]: metric.floatValue });
                    break;
                  case 'utilization': 
                    newMetrics.gpu.utilizations[gpuIndex] = parseFloat(metric.floatValue || 0);
                    updateChartData(time, { [`gpuUtil${gpuIndex}`]: parseFloat(metric.floatValue || 0) });
                    break;
                  case 'vram_utilization': 
                    newMetrics.gpu.vramUtilizations[gpuIndex] = metric.floatValue * 100;
                    updateChartData(time, { [`gpuVram${gpuIndex}`]: metric.floatValue * 100 });
                    break;
                  case 'mclk': newMetrics.gpu.memoryClocks[gpuIndex] = metric.stringValue; break;
                  case 'sclk': newMetrics.gpu.coreClocks[gpuIndex] = metric.stringValue; break;
                }
              }
            }
            break;
        }
        return newMetrics;
      });
    };

    const updateChartData = (time, metrics) => {
      setMetricsData(prevData => {
        const existingIndex = prevData.findIndex(point => point.time === time);
        if (existingIndex >= 0) {
          const newData = [...prevData];
          newData[existingIndex] = { ...newData[existingIndex], ...metrics };
          return newData;
        } else {
          return [...prevData, { time, ...metrics }].slice(-50);
        }
      });
    };

    setupMetricsStream();
    return () => {
      readerActiveRef.current = false;
      if (streamReaderRef.current) streamReaderRef.current.cancel().catch(e => console.log('Error cancelling stream:', e));
      streamReaderRef.current = null;
    };
  }, [nodeId, serverId]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 3 }}>Loading monitoring dashboard...</Typography>
      </Box>
    );
  }

  const renderSummaryCards = () => (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid item xs={12} sm={6} md={3}>
        <MetricCard
          icon={<CpuIcon sx={{ color: theme.palette.primary.main }} />}
          title="CPU Usage"
          value={lastMetrics?.cpu?.usage || 0}
          unit="%"
          percentValue={lastMetrics?.cpu?.usage || 0}
          color={theme.palette.primary.main}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <MetricCard
          icon={<MemoryIcon sx={{ color: theme.palette.info.main }} />}
          title="Memory"
          value={lastMetrics?.memory?.total - lastMetrics?.memory?.free || 0}
          unit="GB"
          percentValue={lastMetrics?.memory?.utilization || 0}
          color={theme.palette.info.main}
          secondaryText={`${formatNumber(lastMetrics?.memory?.total || 0, 'GB')} total`}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <MetricCard
          icon={<DiskIcon sx={{ color: theme.palette.success.main }} />}
          title="Disk"
          value={lastMetrics?.disk?.total - lastMetrics?.disk?.free || 0}
          unit="GB"
          percentValue={lastMetrics?.disk?.utilization || 0}
          color={theme.palette.success.main}
          secondaryText={`${formatNumber(lastMetrics?.disk?.total || 0, 'GB')} total`}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <MetricCard
          icon={<NetworkIcon sx={{ color: theme.palette.warning.main }} />}
          title="Network"
          value={lastMetrics?.network?.received || 0}
          unit="GB"
          color={theme.palette.warning.main}
          secondaryText={`${formatNumber(lastMetrics?.network?.sent || 0, 'GB')} sent`}
        />
      </Grid>
    </Grid>
  );

  const renderChartSection = (title, charts) => (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>{title}</Typography>
      <Grid container spacing={2} alignItems="stretch">
        {charts.map((chart, index) => (
          <Grid item key={index} xs={12} md={chart.width || (charts.length === 2 ? 6 : 4)} sx={{ height: 270 }}>
            <CompactChart 
              data={metricsData} 
              dataKey={chart.dataKey} 
              color={chart.color} 
              title={chart.title} 
              unit={chart.unit} 
              currentValue={chart.value} 
              secondaryValue={chart.secondaryText} 
              icon={chart.icon} 
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderSystemMetrics = () => (
    <Box>
      {renderSummaryCards()}
      
      {renderChartSection("System Performance", [
        {
          dataKey: "cpu",
          color: theme.palette.primary.main,
          title: "CPU Usage",
          unit: "%",
          value: lastMetrics?.cpu?.usage || 0,
          icon: <SpeedIcon sx={{ color: theme.palette.primary.main }} />
        },
        {
          dataKey: "memory",
          color: theme.palette.info.main,
          title: "Memory Usage",
          unit: "%",
          value: lastMetrics?.memory?.utilization || 0,
          secondaryText: `${formatNumber(lastMetrics?.memory?.free || 0, 'GB')} free / ${formatNumber(lastMetrics?.memory?.total || 0, 'GB')} total`,
          icon: <MemoryIcon sx={{ color: theme.palette.info.main }} />
        }
      ])}
      
      {renderChartSection("Storage & Network", [
        {
          dataKey: "disk",
          color: theme.palette.success.main,
          title: "Disk Space",
          unit: "%",
          value: lastMetrics?.disk?.utilization || 0,
          secondaryText: `${formatNumber(lastMetrics?.disk?.free || 0, 'GB')} free / ${formatNumber(lastMetrics?.disk?.total || 0, 'GB')} total`,
          icon: <StorageIcon sx={{ color: theme.palette.success.main }} />
        },
        {
          dataKey: "networkReceived",
          color: theme.palette.warning.main,
          title: "Network Received",
          unit: "GB",
          value: lastMetrics?.network?.received || 0,
          icon: <ReceivedIcon sx={{ color: theme.palette.warning.main }} />
        },
        {
          dataKey: "networkSent",
          color: theme.palette.error.main,
          title: "Network Sent",
          unit: "GB",
          value: lastMetrics?.network?.sent || 0,
          icon: <SentIcon sx={{ color: theme.palette.error.main }} />
        }
      ])}
    </Box>
  );

  const renderGpuMetrics = () => {
    const hasGpuData = lastMetrics.gpu.count > 0 && (lastMetrics.gpu.models.length > 0 || lastMetrics.gpu.temperatures.length > 0 || lastMetrics.gpu.utilizations.length > 0);
    if (!hasGpuData) return <Alert severity="info">No GPU metrics available for this node.</Alert>; 

    return (
      <Box>
        {Array.from({ length: lastMetrics.gpu.count }).map((_, i) => {
          const gpuModel = lastMetrics.gpu.models[i] || `GPU ${i}`;
          const gpuVendor = lastMetrics.gpu.vendor || '';
          const gpuCoreClock = lastMetrics.gpu.coreClocks[i] || 'N/A';
          const gpuMemClock = lastMetrics.gpu.memoryClocks[i] || 'N/A';

          return (
            <Box key={`gpu-section-${i}`} sx={{ mb: i < lastMetrics.gpu.count - 1 ? 4 : 0 }}>
              <Card sx={{ mb: 2, p: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {gpuModel} {gpuVendor ? `(${gpuVendor})` : ''}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Core Clock: {gpuCoreClock} | Memory Clock: {gpuMemClock}
                </Typography>
                
                <Grid container spacing={1}>
                  <Grid item xs={4}>
                    <MetricCard
                      icon={<TempIcon sx={{ color: theme.palette.error.main }} />}
                      title="Temperature"
                      value={lastMetrics.gpu.temperatures[i] || 0}
                      unit="°C"
                      color={theme.palette.error.main}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <MetricCard
                      icon={<GpuIcon sx={{ color: theme.palette.primary.main }} />}
                      title="Utilization"
                      value={lastMetrics.gpu.utilizations[i] || 0}
                      unit="%"
                      percentValue={lastMetrics.gpu.utilizations[i] || 0}
                      color={theme.palette.primary.main}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <MetricCard
                      icon={<VramIcon sx={{ color: theme.palette.secondary.main }} />}
                      title="VRAM"
                      value={lastMetrics.gpu.vramUtilizations[i] || 0}
                      unit="%"
                      percentValue={lastMetrics.gpu.vramUtilizations[i] || 0}
                      color={theme.palette.secondary.main}
                    />
                  </Grid>
                </Grid>
              </Card>
              
              {renderChartSection("", [
                {
                  dataKey: `gpuTemp${i}`,
                  color: theme.palette.error.main,
                  title: "Temperature",
                  unit: "°C",
                  value: lastMetrics.gpu.temperatures[i] || 0,
                  icon: <TempIcon sx={{ color: theme.palette.error.main }} />
                },
                {
                  dataKey: `gpuUtil${i}`,
                  color: theme.palette.primary.main,
                  title: "Utilization",
                  unit: "%",
                  value: lastMetrics.gpu.utilizations[i] || 0,
                  icon: <GpuIcon sx={{ color: theme.palette.primary.main }} />
                },
                {
                  dataKey: `gpuVram${i}`,
                  color: theme.palette.secondary.main,
                  title: "VRAM",
                  unit: "%",
                  value: lastMetrics.gpu.vramUtilizations[i] || 0,
                  icon: <VramIcon sx={{ color: theme.palette.secondary.main }} />
                }
              ])}
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 2, minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: theme.shadows[2] }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DashboardIcon sx={{ fontSize: 28, mr: 1.5, color: theme.palette.primary.main }} />
            <Typography variant="h5">Server Monitoring</Typography>
            <Chip 
              label={isOnline ? "Online" : "Offline"} 
              color={isOnline ? "success" : "error"} 
              icon={isOnline ? <CheckIcon /> : <WarningIcon />} 
              size="small"
              sx={{ ml: 2 }}
            />
          </Box>
          <Badge badgeContent={2} color="warning">
            <IconButton onClick={handleAlertClick}><NotificationsIcon fontSize="large" /></IconButton>
          </Badge>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Node ID: {nodeId || serverId}</Typography>
        {!isOnline && <Alert severity="error" sx={{ mt: 2 }} variant="filled">Unable to connect to monitoring service.</Alert>}
      </Paper>
      
      <Paper sx={{ borderRadius: 2, boxShadow: theme.shadows[2] }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            '& .MuiTab-root': {
              py: 2
            }
          }}
        >
          <Tab label="System" icon={<CpuIcon />} iconPosition="start" />
          <Tab label="GPU" icon={<GpuIcon />} iconPosition="start" />
        </Tabs>
        
        <Box sx={{ p: 3 }}>
          {activeTab === 0 && renderSystemMetrics()}
          {activeTab === 1 && renderGpuMetrics()}
        </Box>
      </Paper>
      
      <Popover 
        open={openAlerts} 
        anchorEl={anchorEl} 
        onClose={handleAlertClose} 
        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}} 
        transformOrigin={{vertical: 'top', horizontal: 'right'}}
      >
        <Paper sx={{ width: 380, maxWidth: '100%' }}>
          <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6">Recent Alerts</Typography>
          </Box>
          <List sx={{ py: 0 }}>
            <ListItem>
              <Alert severity="warning" sx={{ width: '100%' }} variant="outlined">
                <AlertTitle>High CPU Usage</AlertTitle>
                CPU utilization exceeded 80% for 5 minutes
                <Typography variant="caption" display="block">29 minutes ago</Typography>
              </Alert>
            </ListItem>
            <ListItem>
              <Alert severity="info" sx={{ width: '100%' }} variant="outlined">
                <AlertTitle>Disk Space Warning</AlertTitle>
                Disk space below 20% free
                <Typography variant="caption" display="block">51 minutes ago</Typography>
              </Alert>
            </ListItem>
          </List>
        </Paper>
      </Popover>
    </Box>
  );
}
